import { useCallback, useEffect, useState } from "react";
import {
  AUTH_BASE,
  SIBLING_LABEL,
  SIBLING_URL,
  TOKENISER_URL,
} from "./config";

// Distinguishes this primary UI from its sibling. The investors app ships an
// identical file with only this constant changed.
const APP_NAME = "Averer Investors";

interface Account {
  accountId: string;
  email: string;
  logins?: Array<{ provider: string }>;
  wallets?: Array<{ address?: string; walletKind?: string }>;
}

type Status = "loading" | "in" | "out" | "error";

export default function App() {
  const [status, setStatus] = useState<Status>("loading");
  const [account, setAccount] = useState<Account | null>(null);
  const [err, setErr] = useState<string>("");

  const checkSession = useCallback(async () => {
    setStatus("loading");
    setErr("");
    try {
      // Cookie SSO: the shared .averer session cookie rides this cross-origin
      // request automatically (same-site subdomain) — no token, no OIDC.
      const res = await fetch(`${AUTH_BASE}/accounts/me`, {
        credentials: "include",
      });
      if (res.status === 200) {
        setAccount((await res.json()) as Account);
        setStatus("in");
      } else if (res.status === 401) {
        setAccount(null);
        setStatus("out");
      } else {
        setErr(`Unexpected ${res.status} from /accounts/me`);
        setStatus("error");
      }
    } catch (e) {
      setErr(
        `Could not reach the auth service. If this is a CORS error, confirm ${AUTH_BASE} allows this origin with credentials. (${String(e)})`,
      );
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const signIn = useCallback(() => {
    // Central login at the identity front door, same tab. Pass return_to so
    // the SPA redirects back here once the shared .averer cookie is set
    // (AV-193 primary-tier redirect-back).
    const returnTo = encodeURIComponent(window.location.origin + "/");
    window.location.href = `${AUTH_BASE}/ui/?return_to=${returnTo}`;
  }, []);

  const signOut = useCallback(async () => {
    setStatus("loading");
    try {
      await fetch(`${AUTH_BASE}/auth/session`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch {
      /* ignore — re-check reflects the true state */
    }
    await checkSession();
  }, [checkSession]);

  return (
    <div className="wrap">
      <header className="app">
        <h1>{APP_NAME}</h1>
        <span className="badge primary">Primary tier · cookie SSO</span>
      </header>
      <p className="sub">
        A <code>*.staging.redbelly.network</code> app. Login state comes from the
        shared session cookie — no OIDC here.
      </p>

      <div className="card">
        {status === "loading" && (
          <div className="status-line">
            <span className="dot" /> Checking session…
          </div>
        )}

        {status === "in" && account && (
          <>
            <div className="status-line">
              <span className="dot in" /> Signed in
            </div>
            <div className="kv">
              <div>
                <span className="k">Email</span>
                <code>{account.email}</code>
              </div>
              <div>
                <span className="k">Account ID</span>
                <code>{account.accountId}</code>
              </div>
              {account.logins && account.logins.length > 0 && (
                <div>
                  <span className="k">Login providers</span>
                  <code>
                    {account.logins.map((l) => l.provider).join(", ")}
                  </code>
                </div>
              )}
              {account.wallets && account.wallets.length > 0 && (
                <div>
                  <span className="k">Wallets</span>
                  <code>
                    {account.wallets
                      .map((w) => w.address ?? w.walletKind ?? "?")
                      .join(", ")}
                  </code>
                </div>
              )}
            </div>
            <div className="row">
              <button onClick={signOut}>Sign out (all primary)</button>
              <button onClick={() => void checkSession()}>Re-check</button>
            </div>
          </>
        )}

        {status === "out" && (
          <>
            <div className="status-line">
              <span className="dot out" /> Not signed in
            </div>
            <div className="row">
              <button className="primary" onClick={signIn}>
                Sign in at Averer ID
              </button>
              <button onClick={() => void checkSession()}>
                I&apos;ve signed in — re-check
              </button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="status-line">
              <span className="dot out" /> Error
            </div>
            <p className="note">{err}</p>
            <div className="row">
              <button onClick={() => void checkSession()}>Retry</button>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <p className="note">
          <strong>Prove SSO:</strong> once signed in here, open{" "}
          {SIBLING_URL ? (
            <a href={SIBLING_URL} target="_blank" rel="noreferrer">
              {SIBLING_LABEL}
            </a>
          ) : (
            <em>{SIBLING_LABEL}</em>
          )}{" "}
          — it is authenticated instantly via the same <code>.averer</code>{" "}
          cookie, no second login.
          {TOKENISER_URL && (
            <>
              {" "}
              Compare with the external{" "}
              <a href={TOKENISER_URL} target="_blank" rel="noreferrer">
                Tokeniser demo
              </a>{" "}
              (different domain → OIDC, separate session).
            </>
          )}
        </p>
        <p className="note">
          Auth service: <code>{AUTH_BASE}</code>
        </p>
      </div>
    </div>
  );
}
